/**
 * Casca das telas com abas da Área do PCP: Em andamento · Tarefas ·
 * Histórico · Buscar. No desktop as abas ficam no cabeçalho (mesmo padrão
 * do seletor da Capacidade); no celular, no rodapé, no alcance do polegar.
 * O indicador de conexão fica sempre visível no cabeçalho.
 */
import React from "react";
import { FiCheckSquare, FiClock, FiCloudOff, FiList, FiRefreshCw, FiSearch, FiWifi } from "react-icons/fi";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import useMediaQuery from "../../hooks/useMediaQuery";
import { CONEXAO, useAreaPcp } from "./offline/AreaPcpContext";
import { AbasRodape, BlocoTitulo, Controles, LinhaTitulo, Pagina, Segmentado, Topo, celular } from "./styles";

export const BASE = "/legado/area-pcp";

export const ABAS = Object.freeze([
  { id: "em-andamento", rotulo: "Em andamento", icone: FiCheckSquare },
  { id: "tarefas", rotulo: "Tarefas", icone: FiList },
  { id: "historico", rotulo: "Histórico", icone: FiClock },
  { id: "buscar", rotulo: "Buscar", icone: FiSearch },
]);

const Selo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11.5px;
  font-weight: 600;
  white-space: nowrap;
  border: 1px solid var(--${({ $tom }) => $tom}-border);
  background: var(--${({ $tom }) => $tom}-bg);
  color: var(--${({ $tom }) => $tom}-text);
`;

const TEXTO_CONEXAO = {
  [CONEXAO.ONLINE]: { tom: "success", texto: "Online", icone: FiWifi },
  [CONEXAO.OFFLINE]: { tom: "warning", texto: "Sem conexão", icone: FiCloudOff },
  [CONEXAO.PENDENTE]: { tom: "info", texto: "Enviando", icone: FiRefreshCw },
};

/**
 * Online, offline ou "N pendentes de envio": sempre à vista (spec 4.7).
 * `compacto`: só ícone e contagem, para o cabeçalho da execução no celular,
 * onde o nome do lote precisa do espaço (o texto continua para o leitor de tela).
 */
export function IndicadorConexao({ compacto = false }) {
  const { conexao, quantidadePendente } = useAreaPcp();
  const { tom, texto, icone: Icone } = TEXTO_CONEXAO[conexao];
  const pendentes = quantidadePendente
    ? ` · ${quantidadePendente} ${quantidadePendente === 1 ? "pendente" : "pendentes"}`
    : "";
  return (
    <Selo $tom={tom} role="status" aria-live="polite" aria-label={`${texto}${pendentes}`} title={`${texto}${pendentes}`}>
      <Icone size={12} aria-hidden="true" />
      {compacto ? (quantidadePendente || null) : `${texto}${pendentes}`}
    </Selo>
  );
}

function abaAtual(pathname) {
  return ABAS.find((aba) => pathname.startsWith(`${BASE}/${aba.id}`))?.id || "tarefas";
}

export default function Casca({ subtitulo, acoes, contagemAbertas = 0, children }) {
  const ehCelular = useMediaQuery(celular);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const atual = abaAtual(pathname);

  return (
    <Pagina>
      <Topo>
        <LinhaTitulo>
          <BlocoTitulo>
            <h1>Área do PCP</h1>
            <p>{subtitulo || "Manuais e checklists das tarefas de Planejamento e Controle da Produção."}</p>
          </BlocoTitulo>
          <Controles>
            <IndicadorConexao />
            {acoes}
          </Controles>
        </LinhaTitulo>
        {!ehCelular ? (
          <Segmentado role="tablist" aria-label="Seções da Área do PCP">
            {ABAS.map(({ id, rotulo, icone: Icone }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={atual === id}
                onClick={() => navigate(`${BASE}/${id}`)}
              >
                <Icone size={14} aria-hidden="true" />
                {rotulo}
                {id === "em-andamento" && contagemAbertas ? ` · ${contagemAbertas}` : ""}
              </button>
            ))}
          </Segmentado>
        ) : null}
      </Topo>
      {children}
      {ehCelular ? (
        <AbasRodape aria-label="Seções da Área do PCP">
          {ABAS.map(({ id, rotulo, icone: Icone }) => (
            <NavLink key={id} to={`${BASE}/${id}`} aria-current={atual === id ? "page" : undefined}>
              <Icone size={20} aria-hidden="true" />
              {rotulo}
              {id === "em-andamento" && contagemAbertas ? <span className="contador">{contagemAbertas}</span> : null}
            </NavLink>
          ))}
        </AbasRodape>
      ) : null}
    </Pagina>
  );
}
